import type { ReactNode } from "react";
import { cx } from "../cx.ts";

export type Tone = "neutral" | "primary" | "success" | "warning" | "danger";

/**
 * The label reads against the wash, not the solid.
 *
 * A badge sets 12px, and each solid on its own wash measures between 3.3:1 and
 * 4.1:1 — under AA for text that size. The `-ink` tokens are the same hues
 * darkened until they pass; the solids stay for the dot, which is a graphic
 * and only owes 3:1.
 */
const TONES: Record<Tone, string> = {
  neutral: "bg-surface-subtle text-ink-muted",
  primary: "bg-primary-soft text-primary-ink",
  success: "bg-success-soft text-success-ink",
  warning: "bg-warning-soft text-warning-ink",
  danger: "bg-danger-soft text-danger-ink",
};

const DOTS: Record<Tone, string> = {
  neutral: "bg-line-strong",
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

export function Badge({
  tone = "neutral",
  dot = false,
  children,
  className,
}: {
  tone?: Tone;
  /** A status reads faster with one; a currency pair does not need one. */
  dot?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 text-xs font-medium",
        TONES[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cx("size-1.5 rounded-pill", DOTS[tone])} /> : null}
      {children}
    </span>
  );
}
